import { FC, useCallback, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Divider, Stack } from "@mui/material";
import { useFlag } from "@unleash/proxy-client-react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useDidMount } from "rooks";
import { ResourceExplorerAppBar } from "./components/app-bar/ResourceExplorerAppBar";
import { ResourceExplorerCard } from "./components/resource-explorer-card/ResourceExplorerCard";
import { setAzureTagsFeatureToggleToStorage } from "./utils/helpers/azure-tags/setAzureTagsFeatureToggleToStorage";
import { BillingExplorerSideBarPanel } from "./components/BillingExplorerSideBarPanel";
import { useAppSelector } from "@/store/hooks";
import { resourceExplorerViewByIdLoadingSelector } from "@/store/resource-explorer/selectors/resource-explorer-by-id/resourceExplorerViewByIdLoadingSelector";
import { initiateResourceExplorerThunk } from "@/store/resource-explorer/thunks/resource-explorer/initiateResourceExplorerThunk";
import { pathKeyInURL } from "@/store/resource-explorer/thunks/widgets/path/setResourceExplorerPathThunk";
import { isBillingStatusesGettingFulfilledSelector } from "@/store/common/selectors/billing-status/isBillingStatusesGettingFulfilledSelector";
import { resourceExplorerUnMountThunk } from "@/store/resource-explorer/thunks/resource-explorer/resourceExplorerUnMountThunk";
import { currentBillingExplorerSideBarPanelContentTypeSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/currentBillingExplorerSideBarPanelContentTypeSelector";
import { resourceExplorerPossibleGroupingsSelector } from "@/store/resource-explorer/selectors/possible-groupings/resourceExplorerPossibleGroupingsSelector";
import { DimensionDrawers } from "@/components/pages/dimensions/components/form-drawer/DimensionDrawers";
import { getLanguageThunk } from "@/store/language-select/thunks/getLanguageThunk";
import { getBillingExplorerDimensionGroupingsThunk } from "@/store/resource-explorer/thunks/common/getBillingExplorerDimensionGroupingsThunk";

export const ResourceExplorer: FC = () => {
  const dispatch = useDispatch();
  const { viewId = "" } = useParams<{
    viewId: string;
  }>();

  const enableAzureTags = useFlag("EnableAzureBillingExportProcessing");
  const enableCategoryGroupingFilterV2 = useFlag(
    "EnableCategoryGroupingFilterV2",
  );

  const [searchParams] = useSearchParams();
  const path = searchParams.get?.(pathKeyInURL);
  const dimensionId = searchParams.get("dimensionId") as string;
  const categoryId = searchParams.get("categoryId") as string;
  const budgetId = searchParams.get("budgetId") as string;
  const possibleGroupings = useAppSelector(
    resourceExplorerPossibleGroupingsSelector,
  );

  const viewGettingLoading = useAppSelector((state) =>
    resourceExplorerViewByIdLoadingSelector(state, viewId),
  );
  const isBillingStatusesGettingFulfilled = useAppSelector(
    isBillingStatusesGettingFulfilledSelector,
  );
  const sideBarPanelContentType = useAppSelector(
    currentBillingExplorerSideBarPanelContentTypeSelector,
  );

  const dimensionSaveHandler = useCallback(() => {
    dispatch(
      getBillingExplorerDimensionGroupingsThunk({
        enableCategoryGroupingFilterV2,
        forceRefetch: true,
      }),
    );
  }, [dispatch, enableCategoryGroupingFilterV2]);

  useEffect(() => {
    if (viewId && !!possibleGroupings) {
      dispatch(
        initiateResourceExplorerThunk({
          path,
          viewId,
          dimensionId,
          categoryId,
          budgetId,
          enableCategoryGroupingFilterV2,
        }),
      );
    }

    return () => {
      dispatch(resourceExplorerUnMountThunk());
    };
  }, [
    enableCategoryGroupingFilterV2,
    viewId,
    dispatch,
    path,
    dimensionId,
    categoryId,
    budgetId,
    possibleGroupings,
  ]);

  useEffect(() => {
    setAzureTagsFeatureToggleToStorage(enableAzureTags);
  }, [enableAzureTags]);

  useDidMount(() => {
    dispatch(getLanguageThunk());
  });

  if (!viewId || !isBillingStatusesGettingFulfilled) {
    return null;
  }

  return (
    <Stack height="100dvh" overflow="hidden">
      <ResourceExplorerAppBar viewId={viewId} />
      <PanelGroup
        direction="horizontal"
        autoSaveId="biling-explorer-page-navigation"
      >
        <Panel
          style={{ overflowY: "auto" }}
          id="billing-explorer-main-panel"
          order={1}
        >
          {!viewGettingLoading && <ResourceExplorerCard viewId={viewId} />}
        </Panel>

        <PanelResizeHandle disabled={!sideBarPanelContentType}>
          <Divider orientation="vertical" />
        </PanelResizeHandle>

        <BillingExplorerSideBarPanel />
      </PanelGroup>
      <DimensionDrawers onDimensionSave={dimensionSaveHandler} />
    </Stack>
  );
};
